import {
  createSetQueryDataOnUpdate,
  IWork,
  KanbanCardHeight,
  KanbanColumn,
  makeDynamicWorkboardPageStore,
  PageViewModeEnum,
  TKanbanColumnComponentProps,
  useAppStore,
  useIsOnline,
  useFlowCustomView,
  useFlowCustomViewCount,
} from '@diana/core';
import { useMemo, useRef } from 'react';
import { ListOnScrollProps } from 'react-window';
import { useDebouncedCallback } from 'use-debounce';
import { useWorkBoardCreateWork } from './useWorkBoardCreateWork';
import { useWorkboardColumnCountQuery, useWorkboardQuery } from './useWorkboardQuery';
import { getOfflineFilteredWorks } from './Support';
import { useWorkboardCreateWorkOffline } from './useWorkboardCreateWorkOffline';

export function WorkboardKanbanColumn<Item extends IWork>(
  props: TKanbanColumnComponentProps<Item>,
) {
  const { columnComponentProps = {}, ...rest } = props;
  const { flow, stepId, showSidePeek } = columnComponentProps;
  const isOnline = useIsOnline();
  const isOfflineModeEnabled = flow.featureFlags?.isOfflineModeEnabled === true;
  const setLoading = useAppStore((state) => state.setLoading);
  const columnHeight = useRef<number>(0);

  const { canCreateWorkOffline } = useWorkboardCreateWorkOffline({ flowId: flow.id });

  const { data: countData } = useWorkboardColumnCountQuery({
    flow,
    activeStepIds: [stepId],
  });

  const { data, isFetching, fetchNextPage, queryKeys } = useWorkboardQuery({
    flow,
    activeStepIds: [stepId],
    viewMode: PageViewModeEnum.Board,
  });

  const filteredData = useMemo(() => {
    return getOfflineFilteredWorks({ works: data, isOfflineModeEnabled, isOnline });
  }, [data, isOfflineModeEnabled, isOnline]);

  const onCreateItem = useWorkBoardCreateWork({
    flow,
    setProcessing: setLoading,
    onCreated: () => createSetQueryDataOnUpdate({ key: queryKeys as any }),
    showSidePeek,
  });

  const dataLength = filteredData?.length ?? 1;
  const onScroll = useDebouncedCallback(({ scrollOffset }: ListOnScrollProps) => {
    const isAtBottom =
      KanbanCardHeight * dataLength - scrollOffset - columnHeight.current <= KanbanCardHeight * 3;
    if (isAtBottom && !isFetching) {
      fetchNextPage();
    }
  }, 50);

  const onHeightSet = (height: number) => (columnHeight.current = height);

  return (
    <KanbanColumn<Item>
      {...rest}
      count={countData?.count ?? undefined}
      items={(filteredData as Item[]) || []}
      onCreateItem={onCreateItem}
      isLoading={isFetching}
      onScroll={onScroll}
      onHeightSet={onHeightSet}
      createOptions={!isOnline && !canCreateWorkOffline ? [] : rest.createOptions}
    />
  );
}

export function WorkboardKanbanCustomViewColumn<Item extends IWork>(
  props: TKanbanColumnComponentProps<Item>,
) {
  const { columnComponentProps = {}, ...rest } = props;
  const { flow, customView } = columnComponentProps;
  const isOnline = useIsOnline();
  const isOfflineModeEnabled = flow.featureFlags?.isOfflineModeEnabled === true;
  const columnHeight = useRef<number>(0);

  const useWorkboardPageStore = makeDynamicWorkboardPageStore(flow.flowOriginId);
  const filtersValues = useWorkboardPageStore((state) => state.filtersValues);
  const availableFilters = useWorkboardPageStore((state) => state.availableFilters);

  const { data: countData } = useFlowCustomViewCount({
    flow,
    customView,
    filters: availableFilters,
    filtersValues,
  });

  const { data, isFetching, fetchNextPage } = useFlowCustomView({
    flow,
    customView,
    filters: availableFilters,
    filtersValues,
  });

  const filteredData = useMemo(() => {
    return getOfflineFilteredWorks({ works: data, isOfflineModeEnabled, isOnline });
  }, [data, isOfflineModeEnabled, isOnline]);

  const dataLength = filteredData?.length ?? 1;
  const onScroll = useDebouncedCallback(({ scrollOffset }: ListOnScrollProps) => {
    const isAtBottom =
      KanbanCardHeight * dataLength - scrollOffset - columnHeight.current <= KanbanCardHeight * 3;
    if (isAtBottom && !isFetching) {
      fetchNextPage();
    }
  }, 50);

  const onHeightSet = (height: number) => (columnHeight.current = height);

  return (
    <KanbanColumn<Item>
      {...rest}
      count={countData?.count ?? undefined}
      items={(filteredData as Item[]) || []}
      isLoading={isFetching}
      onScroll={onScroll}
      onHeightSet={onHeightSet}
    />
  );
}
